import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import SearchAddress from "components/Common/SearchAddress";
import { useRole } from "components/Hooks/UserHooks";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DatePicker, { registerLocale } from "react-datepicker";
import { formatAddressByNaver, formatDateStringToKorea } from "helpers/format";
import { LIST_OPTION_GENDER } from "helpers/constans";
import { putUser } from "store/thunks";
import addressApi from "api/addressApi";
import UploadFileBK from "components/Common/UploadFileBK";

registerLocale("en", en);
registerLocale("ko", ko);

const StoreInfoTab = ({data, handleUpdateSuccess, setLinkImage} : any) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isOpenSearchAddress, setIsOpenSearchAddress] =
    useState<boolean>(false);
  const [isLoadingMap, setLoadingMap] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    if(!data?.id) return;

    try {
      setIsLoading((_prev) => true);
      const formatValues = {
        ...values,
        thumbnailUrl: values?.thumbnailUrl?.length > 0 ? (values?.thumbnailUrl?.[0]?.url || values?.thumbnailUrl?.[0]) : (values?.thumbnailUrl || ""),
      };

      const response: any = await putUser(data?.id, {
        storeInfo: formatValues
      });
      
      if (response?.code) {
        setIsLoading((_prev) => false);
        toast(
            `${t("The process has been completed.")}`,
            CONFIG_OPTION_TOAST_NORMAL
        );
        handleUpdateSuccess && handleUpdateSuccess();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  const validationSchema = Yup.object({
    files: Yup.array()
      .of(
        Yup.object({
          url: Yup.string().nullable(),
        }).required(`${t("This field is required")}`),
      ).required(`${t("This field is required")}`).max(5, "You can only select up to 5 images").min(1, "This field is required"),
    thumbnailUrl: Yup.array()
      .of(
        Yup.object({
          url: Yup.string().required(`${t("This field is required")}`),
        }).required(`${t("This field is required")}`),
      ).required(`${t("This field is required")}`).min(1, "This field is required"),
    managerName: Yup.string().required(`${t("This field is required")}`),
    managerPhoneNumber: Yup.string().required(`${t("This field is required")}`),
    name: Yup.string().required(`${t("This field is required")}`),
    address: Yup.string().required(`${t("This field is required")}`),
    shortAddress: Yup.string().required(`${t("This field is required")}`),
    managerDateOfBirth: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      thumbnailUrl: null,
      files: null,
      name: "",
      description: "",
      address: "",
      shortAddress: "",
      region1: "",
      region2: "",
      region3: "",
      managerName: "",
      managerPhoneNumber: "",
      managerGender: "male",
      managerDateOfBirth: new Date() || "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const getLatLngByAddress = async (address: string) => {
    try {
      if (address) {
        const data: any = await addressApi.getGEOByAddress({
          query: encodeURIComponent(address),
        });
        let dataAddress = data?.data?.addresses;
        const formatAddress = formatAddressByNaver(dataAddress);

        if (formatAddress) {
          formik?.setFieldValue("address", formatAddress?.address || '');
          formik?.setFieldValue("shortAddress", formatAddress?.short_address || '');
          formik?.setFieldValue("region1", formatAddress?.region1 || "");
          formik?.setFieldValue("region2", formatAddress?.region2 || "");
          formik?.setFieldValue("region3", formatAddress?.region3 || "");
        }
      }
    } catch (error) {
      //
      console.log(error)
    }
  };

  const onSetValueAddress = async (data: any) => {
    if (data) {
      setLoadingMap(true);
      const res = await getLatLngByAddress(data);
      setLoadingMap(false);
    }
    setIsOpenSearchAddress(false);
  };

  useEffect(() => {
    if(data) {
      const dataStore = data?.store;

      formik.setValues({
        ...dataStore,
        thumbnailUrl: dataStore?.thumbnailUrl ? [{url: dataStore?.thumbnailUrl}] : '',
        files: dataStore?.files ? dataStore?.files?.map((item: any) => ({
          ...item,
          newPath: item?.url
        })) : null,
        name: dataStore?.name,
        description: dataStore?.description,
        address: dataStore?.address?.address,
        shortAddress: dataStore?.address?.shortAddress,
        region1: dataStore?.address?.region1,
        region2: dataStore?.address?.region2,
        region3: dataStore?.address?.region3,
        managerName: dataStore?.managerName,
        managerPhoneNumber: dataStore?.managerPhoneNumber,
        managerGender: dataStore?.managerGender,
        managerDateOfBirth: dataStore?.managerDateOfBirth ? new Date(formatDateStringToKorea(dataStore?.managerDateOfBirth)) : null,
      })
    }
  }, [JSON.stringify(data)]);
  
  useEffect(() => {
    document.title = `${t("Profile Info")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);
  
  return (
    <React.Fragment>
      <Row>
        <Col lg={12} className="mt-4 px-4">
          <form onSubmit={formik.handleSubmit}>
            <div
              className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
              style={{
                border: "1px dashed var(--vz-primary)",
                borderRadius: "4px",
              }}
            >
              <span
                className="text-primary position-absolute fs-13 px-1"
                style={{
                  backgroundColor: "var(--vz-secondary-bg)",
                  top: "0px",
                  transform: "translateY(-50%)",
                }}
              >
                {t("Store Info")}
              </span>
              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div>
                  <div className="mb-3" style={{width: '160px'}}>
                    <Label htmlFor="contact_name" className="form-label">
                      {t("Store Photo")} <span className="text-danger">*</span>
                    </Label>
                    <UploadFileBK
                      formik={formik}
                      fieldValue="thumbnailUrl"
                      isMultiple={false}
                      setLinkImage={setLinkImage}
                      isUploadMore={
                        !formik?.values?.thumbnailUrl ||
                        !(formik?.values?.thumbnailUrl as any)?.length
                      }
                    />
                    {formik.touched.thumbnailUrl && formik.errors.thumbnailUrl ? (
                      <div className="text-danger mt-2">
                        {formik.errors.thumbnailUrl}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div className="flex-grow-1">
                  <Row>
                    <Col md={12} lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="name" className="form-label">
                          {t("Store Name")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="name"
                          type="text"
                          className="form-control"
                          id="name"
                          placeholder={t("Store Name")}
                          value={formik?.values?.name}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "name",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <div className="text-danger mt-2">
                            {formik.errors.name}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="description" className="form-label">
                          {t("Introduce")}
                        </Label>
      
                        <div className="d-flex mb-2 gap-2">
                          <textarea
                            className="form-control"
                            name={`description`}
                            maxLength={1000}
                            value={formik?.values?.description}
                            placeholder={`${t("Introduce")}`}
                            onChange={(event) => {
                              formik.setFieldValue(
                                `description`,
                                event?.target?.value
                              );
                            }}
                            style={{ height: 80 }}
                          />
                        </div>
      
                        {formik.touched.description && formik.errors.description ? (
                          <div className="text-danger mt-2">
                            {formik.errors.description}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="address" className="form-label">
                          {t("Address")}
                          <span className="text-danger"> *</span>
                        </Label>
      
                        <div className="d-flex mb-2 gap-2">
                          <Input
                            name="address"
                            type="text"
                            className="form-control"
                            id="address"
                            placeholder={t("Address")}
                            value={formik?.values?.address}
                            onChange={(event: any) =>
                              formik.setFieldValue("address", event?.target?.value)
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-primary whitespace-nowrap"
                            onClick={handleOpenSearchAddress}
                          >
                            {t("Find Address")}
                          </button>
                        </div>
      
                        {formik.touched.address && formik.errors.address ? (
                          <div className="text-danger mt-2">
                            {formik.errors.address}
                          </div>
                        ) : null}
                      </div>
                    </Col>
      
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="shortAddress" className="form-label">
                          {t("Short Address")}
                        </Label>
                        <Input
                          name="shortAddress"
                          type="text"
                          className="form-control"
                          id="shortAddress"
                          placeholder={t("Short Address")}
                          value={formik?.values?.shortAddress}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "shortAddress",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.shortAddress &&
                        formik.errors.shortAddress ? (
                          <div className="text-danger mt-2">
                            {formik.errors.shortAddress}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    {/* {
                      formik?.values?.address && (
                        <>
                          <Col lg={4}>
                            <div className="mb-3">
                              <Label htmlFor="region1" className="form-label">
                                {t("Province/Metropolitan City")}
                              </Label>
                              <Input
                                name="region1"
                                type="text"
                                className="form-control"
                                id="region1"
                                placeholder={t("Province/Metropolitan City")}
                                value={formik?.values?.region1}
                                onChange={(event: any) =>
                                  formik.setFieldValue("region1", event?.target?.value)
                                }
                                disabled={true}
                              />
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div className="mb-3">
                              <Label htmlFor="region2" className="form-label">
                                {t("City/District")}
                              </Label>
                              <Input
                                name="region2"
                                type="text"
                                className="form-control"
                                id="region2"
                                placeholder={t("City/District")}
                                value={formik?.values?.region2}
                                onChange={(event: any) =>
                                  formik.setFieldValue("region2", event?.target?.value)
                                }
                                disabled={true}
                              />
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div className="mb-3">
                              <Label htmlFor="region3" className="form-label">
                                {t("Town/Neighborhood")}
                              </Label>
                              <Input
                                name="region3"
                                type="text"
                                className="form-control"
                                id="region3"
                                placeholder={t("Town/Neighborhood")}
                                value={formik?.values?.region3}
                                onChange={(event: any) =>
                                  formik.setFieldValue("region3", event?.target?.value)
                                }
                                disabled={true}
                              />
                            </div>
                          </Col>
                        </>
                      )
                    } */}

                    <Col md={12} lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="store detail photo" className="form-label">
                          {t("Store Detail Photo")} <span className="text-danger">*</span>
                        </Label>
                        <UploadFileBK
                          formik={formik}
                          fieldValue="files"
                          isMultiple={true}
                          setLinkImage={setLinkImage}
                          isUploadMore={
                            (formik?.values?.files && (formik?.values?.files as any)?.length >= 5 ? false : true) || (!formik?.values?.files)
                          }
                        />
                        {formik.touched.files && formik.errors.files ? (
                          <div className="text-danger mt-2">
                            {formik.errors.files}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="managerName" className="form-label">
                          {t("Contact Name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="managerName"
                          type="text"
                          className="form-control"
                          id="managerName"
                          placeholder={t("Contact Name")}
                          value={formik?.values?.managerName}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "managerName",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.managerName && formik.errors.managerName ? (
                          <div className="text-danger mt-2">
                            {formik.errors.managerName}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="managerPhoneNumber" className="form-label">
                          {t("Phone Number")} {" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="managerPhoneNumber"
                          type="text"
                          className="form-control"
                          id="managerPhoneNumber"
                          placeholder={t("Phone Number")}
                          value={formik?.values?.managerPhoneNumber}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(event: any) => {
                            const inputValue = event.target.value;
                            if (inputValue !== undefined && inputValue.length <= 11 && /^\d*$/.test(inputValue)) {
                              formik.setFieldValue(
                                "managerPhoneNumber",
                                inputValue
                              )
                            }
                          }}
                        />
                        {formik.touched.managerPhoneNumber &&
                        formik.errors.managerPhoneNumber ? (
                          <div className="text-danger mt-2">
                            {formik.errors.managerPhoneNumber}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="managerGender" className="form-label">
                          {t("Gender")} <span className="text-danger">*</span>
                        </Label>
                        <DropdownOptionNew
                          name="managerGender"
                          dataList={LIST_OPTION_GENDER}
                          placeholder={`${t("Gender")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.managerGender}
                          onChangeSelect={(e: any) =>
                            formik.setFieldValue("managerGender", e?.value)
                          }
                          isHasOptionAll={false}
                        />
                        {formik.touched.managerGender && formik.errors.managerGender ? (
                          <div className="text-danger mt-2">
                            {formik.errors.managerGender}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={6} className="datepicker-time">
                      <div className="mb-3">
                        <Label htmlFor="managerDateOfBirth" className="form-label">
                          {t("Birthdate")}{" "}
                          <span className="text-danger">*</span>
                        </Label>

                        <div className="w-100">
                          <DatePicker
                            name={"managerDateOfBirth"}
                            className="form-control"
                            selected={formik?.values?.managerDateOfBirth}
                            placeholderText={t("Birthdate")}
                            onChange={(date: any) => {
                              formik.setFieldValue("managerDateOfBirth", date);
                            }}
                            dateFormat="yyyy-MM-dd"
                            isClearable={true}
                          />
                        </div>

                        {formik.touched.managerDateOfBirth && formik.errors.managerDateOfBirth ? (
                          <div className="text-danger mt-2">
                            {String(formik.errors.managerDateOfBirth)}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

              <Row className="mb-3 mt-2">
                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end">
                    {isHavePermissionRole(
                      ROLES_FOR_APP.USER_UPDATE,
                      userPermissions
                    ) && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading && (
                          <Spinner size="sm me-2"></Spinner>
                        )}
                        {t("Button Save")}
                      </button>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </Col>
      </Row>

      {isOpenSearchAddress && (
        <Modal
          isOpen={!!isOpenSearchAddress}
          centered={true}
          size="lg"
          scrollable={false}
        >
          <ModalHeader toggle={handleCloseModalAddress}>
            {t("Search Address")}
          </ModalHeader>
          <ModalBody>
            <SearchAddress onSetValueAddress={onSetValueAddress} />
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default StoreInfoTab;
